import * as klinecharts from "klinecharts";

export interface TradeArrowSettings {
  orderType: number; // 0=Buy, 1=Sell
  size?: number; // arrow size (default: 15)
  offset?: number; // distance from candle (default: 10)
  tradeInfo?: {
    // additional info for tooltip
    ticket: number;
    lots: number;
    openPrice: number;
    profit?: number;
  };
}

export const registerTradeArrowOverlay = () => {
  if (typeof window === "undefined" || !klinecharts) return;

  try {
    klinecharts.registerOverlay({
      name: "tradeArrow",
      totalStep: 1,
      needDefaultPointFigure: false,
      needDefaultXAxisFigure: false,
      needDefaultYAxisFigure: false,
      createPointFigures: ({
        coordinates,
        bounding,
        barSpace,
        precision,
        overlay,
        xAxis,
        yAxis,
      }: any) => {
        if (!overlay?.points?.[0]) return [];
        const point = overlay.points[0];
        const settings: TradeArrowSettings =
          overlay?.extendData?.settings || {};
        const orderType = settings.orderType ?? 0;
        const size = settings.size || 15;
        const offset = settings.offset || 10;

        // Get candle data at this point
        const dataIndex = point.dataIndex;
        if (dataIndex === undefined) return [];

        // Calculate X position (center of candle)
        const x = xAxis?.convertToPixel(dataIndex);
        if (x === undefined) return [];

        // Get price value and calculate Y position
        const value = point.value;
        const y = yAxis?.convertToPixel(value);
        if (y === undefined) return [];

        // Adjust Y position based on order type
        const adjustedY = orderType === 0 ? y + offset : y - offset;

        // Colors for buy/sell
        const color = orderType === 0 ? "#26a69a" : "#ef5350";

        // Create arrow shape
        const arrowPoints =
          orderType === 0
            ? [
                // Upward arrow (buy) - pointing up from below
                { x: x, y: adjustedY }, // top point
                { x: x - size / 2, y: adjustedY + size }, // bottom left
                { x: x - size / 6, y: adjustedY + size }, // inner left
                { x: x - size / 6, y: adjustedY + size / 2 }, // stem left
                { x: x + size / 6, y: adjustedY + size / 2 }, // stem right
                { x: x + size / 6, y: adjustedY + size }, // inner right
                { x: x + size / 2, y: adjustedY + size }, // bottom right
              ]
            : [
                // Downward arrow (sell) - pointing down from above
                { x: x, y: adjustedY }, // bottom point
                { x: x - size / 2, y: adjustedY - size }, // top left
                { x: x - size / 6, y: adjustedY - size }, // inner left
                { x: x - size / 6, y: adjustedY - size / 2 }, // stem left
                { x: x + size / 6, y: adjustedY - size / 2 }, // stem right
                { x: x + size / 6, y: adjustedY - size }, // inner right
                { x: x + size / 2, y: adjustedY - size }, // top right
              ];

        return [
          {
            type: "polygon",
            attrs: {
              coordinates: arrowPoints,
              color: color,
              style: "fill",
            },
          },
        ];
      },
      onRightClick: ({ overlay, event }: any) => {
        return overlay.id;
      },
    });
  } catch (error) {
    console.error("Failed to register trade arrow overlay:", error);
  }
};
// Helper function to find the correct candle timestamp for a trade
export const findCandleTimestamp = (
  tradeTimestampMs: number,
  timeframe: string
): number => {
  // Trade timestamp is already in milliseconds, candles are also in milliseconds
  const ms = tradeTimestampMs;

  switch (timeframe) {
    case "1m": // 1 minute
      return Math.floor(ms / 60000) * 60000;
    case "5m": // 5 minutes
      return Math.floor(ms / 300000) * 300000;
    case "15m": // 15 minutes
      return Math.floor(ms / 900000) * 900000;
    case "30m": // 30 minutes
      return Math.floor(ms / 1800000) * 1800000;
    case "1h": // 1 hour
      return Math.floor(ms / 3600000) * 3600000;
    case "2h": // 2 hours
      return Math.floor(ms / 7200000) * 7200000;
    case "4h": // 4 hours
      return Math.floor(ms / 14400000) * 14400000;
    case "12h": // 12 hours
      return Math.floor(ms / 43200000) * 43200000;
    case "1d": // 1 day
      return Math.floor(ms / 86400000) * 86400000;
    case "1mn": // 1 month (approximate as 30 days)
      return Math.floor(ms / 2592000000) * 2592000000;
    default:
      return Math.floor(ms / 60000) * 60000; // default to 1 minute
  }
};

// Helper function to create trade arrow on chart
export const createTradeArrow = (chart: any, trade: any, timeframe: string) => {
  if (!chart || !trade) return null;

  // Convert trade timestamp to candle timestamp
  const candleTimestamp = findCandleTimestamp(
    trade.openTimestampUTC,
    timeframe
  );

  // Get candle data to find high/low
  const dataList = chart.getDataList();

  const candle = dataList.find((d: any) => d.timestamp === candleTimestamp);

  if (!candle) {
    // Try to find nearest candle
    const nearestCandle = dataList.reduce((prev: any, curr: any) => {
      return Math.abs(curr.timestamp - candleTimestamp) <
        Math.abs(prev.timestamp - candleTimestamp)
        ? curr
        : prev;
    }, dataList[0]);

    if (
      nearestCandle &&
      Math.abs(nearestCandle.timestamp - candleTimestamp) < 300000
    ) {
      // Within 5 minutes (in milliseconds)
      // Use nearest candle
      const price =
        trade.orderType === 0 ? nearestCandle.low : nearestCandle.high;

      const id = `trade_${trade.ticket}_${Date.now()}`;

      const overlayConfig = {
        id,
        name: "tradeArrow",
        points: [
          {
            timestamp: nearestCandle.timestamp,
            value: price,
          },
        ],
        extendData: {
          settings: {
            orderType: trade.orderType,
            size: 15,
            offset: 10,
            tradeInfo: {
              ticket: trade.ticket,
              lots: trade.lots,
              openPrice: trade.openPrice,
              profit: trade.profit,
            },
          },
        },
        lock: true,
        visible: true,
        zLevel: 20,
        mode: "normal",
        modeSensitivity: 0,
        styles: {},
      };

      chart.createOverlay(overlayConfig);
      return id;
    }

    console.warn(`No candle found for trade at timestamp ${candleTimestamp}`);
    return null;
  }

  // Determine price position based on order type
  const price = trade.orderType === 0 ? candle.low : candle.high;

  const id = `trade_${trade.ticket}_${Date.now()}`;

  const overlayConfig = {
    id,
    name: "tradeArrow",
    points: [
      {
        timestamp: candleTimestamp,
        value: price,
      },
    ],
    extendData: {
      settings: {
        orderType: trade.orderType,
        size: 15,
        offset: 10,
        tradeInfo: {
          ticket: trade.ticket,
          lots: trade.lots,
          openPrice: trade.openPrice,
          profit: trade.profit,
        },
      },
    },
    lock: true,
    visible: true,
    zLevel: 20,
    mode: "normal",
    modeSensitivity: 0,
    styles: {},
  };

  chart.createOverlay(overlayConfig);
  return id;
};

// Track arrow IDs to manage them
const tradeArrowIds = new Set<string>();

// Helper function to update all trade arrows
export const updateTradeArrows = (
  chart: any,
  trades: any[],
  selectedSymbol: string,
  timeframe: string
) => {
  if (!chart || !trades) return [];

  // Remove existing trade arrows
  tradeArrowIds.forEach((id) => {
    try {
      chart.removeOverlay(id);
    } catch (error) {
      console.warn(`Failed to remove trade arrow ${id}:`, error);
    }
  });
  tradeArrowIds.clear();

  // Filter trades for selected symbol
  const symbolTrades = trades.filter(
    (trade) => trade.symbol === selectedSymbol
  );

  // Create arrows for each trade
  const ids: string[] = [];
  symbolTrades.forEach((trade) => {
    const id = createTradeArrow(chart, trade, timeframe);
    if (id) {
      ids.push(id);
      tradeArrowIds.add(id);
    }
  });

  return ids;
};
